import { Brand } from "effect";

/**
 * Branded ISO calendar date in `yyyy-mm-dd` form.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const date = parseIsoDate("2026-02-28");
 * ```
 */
export type IsoDate = string & Brand.Brand<"IsoDate">;

/**
 * Branded local wall-clock time in `HH:mm` form.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const time = parseLocalTime("08:30");
 * ```
 */
export type LocalTime = string & Brand.Brand<"LocalTime">;

/**
 * Branded UTC instant in ISO date-time form.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const instant = parseUtcInstant("2026-05-15T10:00:00.000Z");
 * ```
 */
export type UtcInstant = string & Brand.Brand<"UtcInstant">;

/**
 * Branded IANA timezone identifier.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const zone = parseIanaTimeZone("Europe/Berlin");
 * ```
 */
export type IanaTimeZone = string & Brand.Brand<"IanaTimeZone">;

/**
 * Injectable source for current time.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const clock = testClock("2026-03-30T10:00:00.000Z");
 * ```
 */
export interface Clock {
  now(): Date;
}

/**
 * Named practice timezone fallback until tenant/practice timezone ownership exists.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * todayIsoDate(testClock("2026-03-29T22:30:00.000Z"), practiceTimeZone);
 * ```
 */
export const practiceTimeZone = parseIanaTimeZone("Europe/Berlin");

/**
 * System clock adapter for non-test call sites.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const today = todayIsoDate(systemClock, practiceTimeZone);
 * ```
 */
export const systemClock: Clock = {
  now: () => new Date(),
};

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const swsDatePattern = /^(\d{2})(\d{2})(\d{4})$/;
const localTimePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
const fhirDateTimePattern =
  /^(\d{4})-(\d{2})-(\d{2})(?:T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.(\d{1,9}))?(Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)?)?$/;
// FHIR R5 `instant` is xs:dateTime with unrestricted fractional digits, and
// PostgreSQL `TIMESTAMPTZ NOW()` round-trips with microsecond precision (6
// digits). We accept 0–9 fractional digits on input and truncate to the
// millisecond resolution that JavaScript `Date` supports inside
// `parseUtcInstant`. The canonical output format remains 3-digit `.000Z` via
// `formatUtcInstant`.
const utcInstantPattern =
  /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(?:\.(\d{1,9}))?Z$/;

function partsToIsoDate(year: number, month: number, day: number): IsoDate {
  const yyyy = String(year).padStart(4, "0");
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}` as IsoDate;
}

function utcDateFromParts(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
): Date {
  const date = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second, millisecond),
  );
  if (year >= 0 && year < 100) {
    date.setUTCFullYear(year, month - 1, day);
    date.setUTCHours(hour, minute, second, millisecond);
  }
  return date;
}

function localDateFromParts(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
): Date {
  const date = new Date(year, month - 1, day, hour, minute, second, millisecond);
  if (year >= 0 && year < 100) {
    date.setFullYear(year, month - 1, day);
    date.setHours(hour, minute, second, millisecond);
  }
  return date;
}

interface CalendarDateTimeParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function calendarDateTimePartsInTimeZone(
  date: Date,
  timeZone: IanaTimeZone,
): CalendarDateTimeParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(date);

  const values = new Map(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(values.get("year")),
    month: Number(values.get("month")),
    day: Number(values.get("day")),
    hour: Number(values.get("hour")),
    minute: Number(values.get("minute")),
    second: Number(values.get("second")),
  };
}

function utcMillisForTimeZoneDateTime(
  parts: CalendarDateTimeParts,
  millisecond: number,
  timeZone: IanaTimeZone,
  original: string,
): number {
  const localWallTimeAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    millisecond,
  );

  let candidate = localWallTimeAsUtc;
  for (let attempt = 0; attempt < 4; attempt++) {
    const observed = calendarDateTimePartsInTimeZone(
      new Date(candidate),
      timeZone,
    );
    const observedAsUtc = Date.UTC(
      observed.year,
      observed.month - 1,
      observed.day,
      observed.hour,
      observed.minute,
      observed.second,
      millisecond,
    );
    const offset = observedAsUtc - candidate;
    const next = localWallTimeAsUtc - offset;
    if (next === candidate) {
      break;
    }
    candidate = next;
  }

  const roundTrip = calendarDateTimePartsInTimeZone(
    new Date(candidate),
    timeZone,
  );
  const isRealZonedDateTime =
    roundTrip.year === parts.year &&
    roundTrip.month === parts.month &&
    roundTrip.day === parts.day &&
    roundTrip.hour === parts.hour &&
    roundTrip.minute === parts.minute &&
    roundTrip.second === parts.second;

  if (!isRealZonedDateTime) {
    throw new Error(`Invalid FHIR dateTime in timezone ${timeZone}: ${original}`);
  }

  return candidate;
}

function assertRealCalendarDate(
  year: number,
  month: number,
  day: number,
  original: string,
  label: string,
): IsoDate {
  const date = utcDateFromParts(year, month, day);
  const isRealDate =
    year >= 1 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;

  if (!isRealDate) {
    throw new Error(`Invalid ${label}: ${original}`);
  }

  return partsToIsoDate(year, month, day);
}

/**
 * Parses an ISO calendar date and rejects impossible dates.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * parseIsoDate("2026-02-28");
 * ```
 */
export function parseIsoDate(value: string): IsoDate {
  const match = isoDatePattern.exec(value);
  if (!match) {
    throw new Error(`Invalid ISO date: ${value}`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  return assertRealCalendarDate(year, month, day, value, "ISO date");
}

/**
 * Converts an SWS `ddmmyyyy` value to a validated ISO calendar date.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * parseSwsDate("29022024");
 * ```
 */
export function parseSwsDate(value: string): IsoDate {
  const match = swsDatePattern.exec(value);
  if (!match) {
    throw new Error(`Invalid SWS date: ${value}`);
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  return assertRealCalendarDate(year, month, day, value, "SWS date");
}

/**
 * Parses a local wall-clock time in `HH:mm` form.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * parseLocalTime("08:30");
 * ```
 */
export function parseLocalTime(value: string): LocalTime {
  if (!localTimePattern.test(value)) {
    throw new Error(`Invalid local time: ${value}`);
  }

  return value as LocalTime;
}

/**
 * Parses an IANA timezone identifier.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * parseIanaTimeZone("Europe/Berlin");
 * ```
 */
export function parseIanaTimeZone(value: string): IanaTimeZone {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone: value });
    formatter.resolvedOptions();
  } catch {
    throw new Error(`Invalid IANA timezone: ${value}`);
  }

  return value as IanaTimeZone;
}

/**
 * Parses an absolute UTC instant.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * parseUtcInstant("2026-05-15T10:00:00.000Z");
 * ```
 */
export function parseUtcInstant(value: string): UtcInstant {
  const match = utcInstantPattern.exec(value);
  if (!match) {
    throw new Error(`Invalid UTC instant: ${value}`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  // Truncate any sub-millisecond precision (µs/ns from PostgreSQL TIMESTAMPTZ
  // round-trips) to the millisecond resolution JavaScript `Date` supports.
  // The first three digits become the millisecond value; everything beyond is
  // discarded. Missing fraction defaults to zero.
  const fractionalDigits = match[7] ?? "";
  const millisecond = fractionalDigits
    ? Number(fractionalDigits.slice(0, 3).padEnd(3, "0"))
    : 0;
  // Build a strict 3-digit-fraction normalized form so `Date.parse` is robust
  // across engines that reject 6+-digit fractions (some Node/Bun builds do).
  const normalized = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}.${String(millisecond).padStart(3, "0")}Z`;
  const millis = Date.parse(normalized);
  if (!Number.isFinite(millis)) {
    throw new Error(`Invalid UTC instant: ${value}`);
  }

  const date = new Date(millis);
  const isRealUtcInstant =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute &&
    date.getUTCSeconds() === second &&
    date.getUTCMilliseconds() === millisecond;

  if (!isRealUtcInstant) {
    throw new Error(`Invalid UTC instant: ${value}`);
  }

  return value as UtcInstant;
}

/**
 * Creates a deterministic test clock.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * const clock = testClock("2026-03-30T10:00:00.000Z");
 * ```
 */
export function testClock(instant: string | Date): Clock {
  const date =
    instant instanceof Date ? new Date(instant.getTime()) : new Date(instant);
  if (!Number.isFinite(date.getTime())) {
    throw new Error(`Invalid test clock instant: ${String(instant)}`);
  }

  return {
    now: () => new Date(date.getTime()),
  };
}

/**
 * Returns today's ISO calendar date in the given timezone.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * todayIsoDate(testClock("2026-03-29T22:30:00.000Z"), practiceTimeZone);
 * ```
 */
export function todayIsoDate(clock: Clock, timeZone: IanaTimeZone): IsoDate {
  const parts = calendarDateTimePartsInTimeZone(clock.now(), timeZone);

  return assertRealCalendarDate(
    parts.year,
    parts.month,
    parts.day,
    `${parts.year}-${parts.month}-${parts.day}`,
    "timezone date",
  );
}

/**
 * Formats a Date's UTC calendar day as an ISO date.
 *
 * Use this for protocol, import, and server boundaries whose Date value is
 * already defined as a UTC instant and the desired date is the UTC day.
 *
 * @since 2026-05-23
 * @example
 * ```ts
 * formatUtcDateAsIsoDate(new Date("2026-03-29T22:30:00.000Z"));
 * ```
 */
export function formatUtcDateAsIsoDate(date: Date): IsoDate {
  return assertRealCalendarDate(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.toISOString(),
    "UTC date",
  );
}

/**
 * Formats a Date's local calendar day as an ISO date.
 *
 * Use this for date-picker and calendar UI values where the Date represents a
 * local wall-clock day and converting through UTC would shift the business day.
 *
 * @since 2026-05-23
 * @example
 * ```ts
 * formatLocalDateAsIsoDate(new Date(2026, 2, 30));
 * ```
 */
export function formatLocalDateAsIsoDate(date: Date): IsoDate {
  return assertRealCalendarDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.toString(),
    "local date",
  );
}

/**
 * Converts an ISO calendar date to a local Date for legacy calendar widgets.
 *
 * @since 2026-05-25
 * @example
 * ```ts
 * isoDateToLocalDate("2026-05-24");
 * ```
 */
export function isoDateToLocalDate(value: string): Date {
  const [yearText, monthText, dayText] = parseIsoDate(value).split("-");

  return localDateFromParts(
    Number(yearText),
    Number(monthText),
    Number(dayText),
  );
}

/**
 * Formats a Date as a validated UTC instant string.
 *
 * Use this when product code needs a timestamp boundary but should not own
 * raw `Date#toISOString` formatting.
 *
 * @since 2026-05-23
 * @example
 * ```ts
 * formatUtcInstant(new Date("2026-05-23T10:15:30.000Z"));
 * ```
 */
export function formatUtcInstant(date: Date): UtcInstant {
  return parseUtcInstant(date.toISOString());
}

/**
 * Formats a UTC instant as the local calendar date observed by the browser or
 * Node runtime.
 *
 * Use this for calendar UI grouping where upstream slot instants must be shown
 * on the user's local wall-clock day.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * formatUtcInstantAsLocalIsoDate("2026-05-24T08:30:00.000Z");
 * ```
 */
export function formatUtcInstantAsLocalIsoDate(value: string): IsoDate {
  return formatLocalDateAsIsoDate(parseUtcInstantDate(value));
}

/**
 * Formats a UTC instant as an ISO date in an explicit IANA timezone.
 *
 * Use this for regulated local-day processing where the timezone is part of
 * the rule and must not depend on the host runtime.
 *
 * @since 2026-06-26
 * @example
 * ```ts
 * formatUtcInstantAsIsoDateInTimeZone("2026-03-29T22:30:00.000Z", practiceTimeZone);
 * ```
 */
export function formatUtcInstantAsIsoDateInTimeZone(
  value: string,
  timeZone: IanaTimeZone,
): IsoDate {
  const parts = calendarDateTimePartsInTimeZone(
    parseUtcInstantDate(value),
    timeZone,
  );

  return assertRealCalendarDate(
    parts.year,
    parts.month,
    parts.day,
    value,
    "timezone date",
  );
}

/**
 * Parses a FHIR `date` or `dateTime` as a UTC instant using an explicit
 * timezone when the value has no timezone information.
 *
 * Date-only values resolve to the start of that local day. Date-time values
 * with `Z` or an explicit offset are treated as absolute instants. Date-time
 * values without a timezone are interpreted as local wall-clock time in the
 * supplied IANA timezone.
 *
 * @since 2026-06-26
 * @example
 * ```ts
 * parseFhirDateTimeAsUtcInstantInTimeZone("2026-07-15T10:30:00", practiceTimeZone);
 * ```
 */
export function parseFhirDateTimeAsUtcInstantInTimeZone(
  value: string,
  timeZone: IanaTimeZone,
): UtcInstant {
  const match = fhirDateTimePattern.exec(value);
  if (!match) {
    throw new Error(`Invalid FHIR dateTime: ${value}`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  assertRealCalendarDate(year, month, day, value, "FHIR dateTime");

  const hour = match[4] === undefined ? 0 : Number(match[4]);
  const minute = match[5] === undefined ? 0 : Number(match[5]);
  const second = match[6] === undefined ? 0 : Number(match[6]);
  const fractionalDigits = match[7] ?? "";
  const millisecond = fractionalDigits
    ? Number(fractionalDigits.slice(0, 3).padEnd(3, "0"))
    : 0;
  const explicitZone = match[8];

  if (explicitZone) {
    const normalized = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}.${String(millisecond).padStart(3, "0")}${explicitZone}`;
    const millis = Date.parse(normalized);
    if (!Number.isFinite(millis)) {
      throw new Error(`Invalid FHIR dateTime: ${value}`);
    }
    return formatUtcInstant(new Date(millis));
  }

  const millis = utcMillisForTimeZoneDateTime(
    { year, month, day, hour, minute, second },
    millisecond,
    timeZone,
    value,
  );

  return formatUtcInstant(new Date(millis));
}

/**
 * Formats a FHIR `date` or `dateTime` as an ISO calendar date in an explicit
 * timezone.
 *
 * Use this for FHIR resources where date-time values without an explicit
 * timezone must be interpreted in a domain timezone before comparing or
 * persisting the calendar date.
 *
 * @since 2026-06-26
 * @example
 * ```ts
 * formatFhirDateTimeAsIsoDateInTimeZone("2026-07-15T10:30:00", practiceTimeZone);
 * ```
 */
export function formatFhirDateTimeAsIsoDateInTimeZone(
  value: string,
  timeZone: IanaTimeZone,
): IsoDate {
  return formatUtcInstantAsIsoDateInTimeZone(
    parseFhirDateTimeAsUtcInstantInTimeZone(value, timeZone),
    timeZone,
  );
}

/**
 * Formats a UTC instant as local `HH:mm` wall-clock time.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * formatUtcInstantAsLocalTime("2026-05-24T08:30:00.000Z");
 * ```
 */
export function formatUtcInstantAsLocalTime(value: string): LocalTime {
  const date = parseUtcInstantDate(value);
  return parseLocalTime(
    `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`,
  );
}

/**
 * Formats a validated ISO date for German calendar display without relying on
 * host timezone parsing.
 *
 * @since 2026-05-25
 * @example
 * ```ts
 * formatIsoDateForGermanDisplay("2026-05-24");
 * ```
 */
export function formatIsoDateForGermanDisplay(value: string): string {
  const [yearText, monthText, dayText] = parseIsoDate(value).split("-");

  return `${dayText}.${monthText}.${yearText}`;
}

/**
 * Formats a validated UTC instant for German date-time display.
 *
 * @since 2026-05-25
 * @example
 * ```ts
 * formatUtcInstantForGermanDisplay("2026-05-24T08:30:00.000Z");
 * ```
 */
export function formatUtcInstantForGermanDisplay(value: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parseUtcInstantDate(value));
}

/**
 * Returns the local wall-clock hour for a UTC instant.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * localHourOfUtcInstant("2026-05-24T08:30:00.000Z");
 * ```
 */
export function localHourOfUtcInstant(value: string): number {
  return parseUtcInstantDate(value).getHours();
}

/**
 * Returns the epoch milliseconds for a validated UTC instant.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * epochMillisOfUtcInstant("2026-05-24T08:30:00.000Z");
 * ```
 */
export function epochMillisOfUtcInstant(value: string): number {
  return parseUtcInstantDate(value).getTime();
}

/**
 * Parses a validated UTC instant into a Date object.
 *
 * Use this at API and persistence boundaries that still expose `Date` while
 * keeping raw string parsing inside the central date-time-validity module.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * parseUtcInstantDate("2026-05-23T10:15:30.000Z");
 * ```
 */
export function parseUtcInstantDate(value: string): Date {
  return new Date(parseUtcInstant(value));
}

/**
 * Returns a Date offset from a clock by a millisecond duration.
 *
 * Use this for legacy APIs that require `Date` expiry values while centralizing
 * raw Date construction in the date-time-validity boundary.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * dateAfterMillis(testClock("2026-05-23T10:15:30.000Z"), 3_600_000);
 * ```
 */
export function dateAfterMillis(clock: Clock, millis: number): Date {
  return new Date(clock.now().getTime() + millis);
}

/**
 * Returns a Date for epoch milliseconds behind the central boundary.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * dateFromEpochMillis(1_778_931_200_000);
 * ```
 */
export function dateFromEpochMillis(millis: number): Date {
  return new Date(millis);
}

/**
 * Returns a Date on the same local calendar as `baseDate`, offset by whole
 * days and set to a local wall-clock time.
 *
 * Use this for legacy calendar UI/demo boundaries that still exchange `Date`
 * objects while keeping raw Date mutation in the central helper module.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * dateAtLocalDayTimeOffset(new Date("2026-05-24T10:00:00.000Z"), 1, 9, 30);
 * ```
 */
export function dateAtLocalDayTimeOffset(
  baseDate: Date,
  dayOffset: number,
  hour: number,
  minute: number,
): Date {
  const next = new Date(baseDate.getTime());
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + dayOffset);
  next.setHours(hour, minute, 0, 0);

  return next;
}

function epochDay(value: IsoDate): number {
  const [yearText, monthText, dayText] = value.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);

  return Math.trunc(utcDateFromParts(year, month, day).getTime() / 86_400_000);
}

/**
 * Adds whole UTC calendar days to an ISO date.
 *
 * Use this for date-only protocol fields where adding days must not depend on
 * process locale, DST, or raw Date mutation.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * addDaysToIsoDate("2026-02-28", 1);
 * ```
 */
export function addDaysToIsoDate(currentDate: string, days: number): IsoDate {
  const targetEpochDay = epochDay(parseIsoDate(currentDate)) + days;

  return formatUtcDateAsIsoDate(new Date(targetEpochDay * 86_400_000));
}

/**
 * Adds whole UTC calendar months to an ISO date using JavaScript Date month
 * rollover semantics behind the central date-time-validity boundary.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * addMonthsToIsoDate("2026-01-31", 1);
 * ```
 */
export function addMonthsToIsoDate(
  currentDate: string,
  months: number,
): IsoDate {
  const [yearText, monthText, dayText] = parseIsoDate(currentDate).split("-");
  const date = utcDateFromParts(
    Number(yearText),
    Number(monthText),
    Number(dayText),
  );
  date.setUTCMonth(date.getUTCMonth() + months);

  return formatUtcDateAsIsoDate(date);
}

/**
 * Returns the first day of an ISO date's calendar month.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * firstDayOfIsoMonth("2026-05-24");
 * ```
 */
export function firstDayOfIsoMonth(currentDate: string): IsoDate {
  const [yearText, monthText] = parseIsoDate(currentDate).split("-");
  return parseIsoDate(`${yearText}-${monthText}-01`);
}

/**
 * Returns the last day of an ISO date's calendar month.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * lastDayOfIsoMonth("2026-02-10");
 * ```
 */
export function lastDayOfIsoMonth(currentDate: string): IsoDate {
  const [yearText, monthText] = parseIsoDate(currentDate).split("-");
  const date = utcDateFromParts(Number(yearText), Number(monthText) + 1, 0);
  return formatUtcDateAsIsoDate(date);
}

/**
 * Returns the day-of-month component for an ISO date.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * dayOfIsoDate("2026-05-24");
 * ```
 */
export function dayOfIsoDate(currentDate: string): number {
  return Number(parseIsoDate(currentDate).split("-")[2]);
}

/**
 * Returns the ISO weekday number where Monday is 1 and Sunday is 7.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * isoWeekday("2026-05-24");
 * ```
 */
export function isoWeekday(currentDate: string): number {
  return ((epochDay(parseIsoDate(currentDate)) + 3) % 7) + 1;
}

/**
 * Returns the ISO week number for an ISO calendar date.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * isoWeekNumber("2026-05-24");
 * ```
 */
export function isoWeekNumber(currentDate: string): number {
  const currentEpochDay = epochDay(parseIsoDate(currentDate));
  const thursdayEpochDay = currentEpochDay + (4 - isoWeekday(currentDate));
  const thursdayDate = formatUtcDateAsIsoDate(
    new Date(thursdayEpochDay * 86_400_000),
  );
  const isoYear = Number(thursdayDate.split("-")[0]);
  const firstThursdayEpochDay =
    epochDay(parseIsoDate(`${isoYear}-01-04`)) +
    (4 - isoWeekday(`${isoYear}-01-04`));

  return Math.floor((thursdayEpochDay - firstThursdayEpochDay) / 7) + 1;
}

/**
 * Formats an ISO calendar date for UI display.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * formatIsoDateForLocale("2026-05-24", "de", { weekday: "short" });
 * ```
 */
export function formatIsoDateForLocale(
  currentDate: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const [yearText, monthText, dayText] = parseIsoDate(currentDate).split("-");
  const date = utcDateFromParts(
    Number(yearText),
    Number(monthText),
    Number(dayText),
    12,
  );

  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    ...options,
  }).format(date);
}

/**
 * Formats a UTC instant for UI display.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * formatUtcInstantForLocale("2026-05-24T08:30:00.000Z", "de-DE", { hour: "2-digit" });
 * ```
 */
export function formatUtcInstantForLocale(
  value: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(locale, options).format(
    parseUtcInstantDate(value),
  );
}

/**
 * Formats the start of a local ISO date as a UTC instant string.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * localIsoDateStartAsUtcInstant("2026-05-24");
 * ```
 */
export function localIsoDateStartAsUtcInstant(currentDate: string): UtcInstant {
  const [yearText, monthText, dayText] = parseIsoDate(currentDate).split("-");
  return formatUtcInstant(
    localDateFromParts(
      Number(yearText),
      Number(monthText),
      Number(dayText),
      0,
      0,
      0,
      0,
    ),
  );
}

/**
 * Formats the end of a local ISO date as a UTC instant string.
 *
 * @since 2026-05-24
 * @example
 * ```ts
 * localIsoDateEndAsUtcInstant("2026-05-24");
 * ```
 */
export function localIsoDateEndAsUtcInstant(currentDate: string): UtcInstant {
  const [yearText, monthText, dayText] = parseIsoDate(currentDate).split("-");
  return formatUtcInstant(
    localDateFromParts(
      Number(yearText),
      Number(monthText),
      Number(dayText),
      23,
      59,
      59,
      999,
    ),
  );
}

const zonedWallClockAsUtcMilliseconds = (
  instant: Date,
  timeZone: IanaTimeZone,
): number => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(instant);
  const values = new Map(parts.map((part) => [part.type, part.value]));
  return Date.UTC(
    Number(values.get("year")),
    Number(values.get("month")) - 1,
    Number(values.get("day")),
    Number(values.get("hour")),
    Number(values.get("minute")),
    Number(values.get("second")),
  );
};

/**
 * Formats the end of an ISO date in a named timezone as a canonical UTC instant.
 *
 * @since 2026-07-31
 * @example
 * ```ts
 * zonedIsoDateEndAsUtcInstant("2026-07-28", parseIanaTimeZone("Europe/Berlin"));
 * // "2026-07-28T21:59:59.999Z"
 * ```
 */
export function zonedIsoDateEndAsUtcInstant(
  currentDate: string,
  timeZone: IanaTimeZone,
): UtcInstant {
  const [yearText, monthText, dayText] = parseIsoDate(currentDate).split("-");
  const targetWallClock = Date.UTC(
    Number(yearText),
    Number(monthText) - 1,
    Number(dayText),
    23,
    59,
    59,
  );
  let candidate = targetWallClock;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const observedWallClock = zonedWallClockAsUtcMilliseconds(
      new Date(candidate),
      timeZone,
    );
    const correction = targetWallClock - observedWallClock;
    candidate += correction;
    if (correction === 0) break;
  }

  if (
    zonedWallClockAsUtcMilliseconds(new Date(candidate), timeZone) !==
    targetWallClock
  ) {
    throw new Error(
      `Unable to resolve end of ${currentDate} in timezone ${timeZone}`,
    );
  }

  return formatUtcInstant(new Date(candidate + 999));
}

/**
 * Returns true when `candidate` is after `reference`.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * isDateAfter("2026-03-31", "2026-03-30");
 * ```
 */
export function isDateAfter(candidate: string, reference: string): boolean {
  return epochDay(parseIsoDate(candidate)) > epochDay(parseIsoDate(reference));
}

/**
 * Returns the local-day distance from `currentDate` to `targetDate`.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * daysUntilDate("2026-03-30", "2026-04-06");
 * ```
 */
export function daysUntilDate(currentDate: string, targetDate: string): number {
  return (
    epochDay(parseIsoDate(targetDate)) - epochDay(parseIsoDate(currentDate))
  );
}

/**
 * Returns true when `candidate` is within the inclusive date range.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * containsDateInclusive("2026-03-30", "2026-03-29", "2026-03-30");
 * ```
 */
export function containsDateInclusive(
  candidate: string,
  startDate: string,
  endDate: string,
): boolean {
  const candidateDay = epochDay(parseIsoDate(candidate));

  return (
    candidateDay >= epochDay(parseIsoDate(startDate)) &&
    candidateDay <= epochDay(parseIsoDate(endDate))
  );
}

/**
 * Returns true when `validUntil` is at or before `now`.
 *
 * @since 2026-05-16
 * @example
 * ```ts
 * isExpiredAtInstant("2026-05-15T10:00:00.000Z", new Date());
 * ```
 */
export function isExpiredAtInstant(validUntil: string, now: Date): boolean {
  return Date.parse(parseUtcInstant(validUntil)) <= now.getTime();
}

/**
 * Resolve a zoned calendar date and minute for an inclusive business-time release.
 * A repeated minute selects its first occurrence. A nonexistent minute resolves
 * to the first valid instant after the gap, rather than shifting by the gap size.
 * Strict FHIR parsing keeps its existing rejection semantics.
 *
 * @since 2026-10-05
 * @example
 * ```ts
 * resolveZonedReleaseInstant("2026-03-29", "02:30", practiceTimeZone);
 * // "2026-03-29T01:00:00.000Z"
 * ```
 */
export function resolveZonedReleaseInstant(
  date: string,
  time: string,
  timeZone: IanaTimeZone,
): UtcInstant {
  const validatedDate = parseIsoDate(date);
  const validatedTime = parseLocalTime(time);
  const [year, month, day] = validatedDate.split("-").map(Number);
  const [hour, minute] = validatedTime.split(":").map(Number);
  if (year === undefined || month === undefined || day === undefined ||
      hour === undefined || minute === undefined) throw new Error("Invalid release time");
  const target = utcDateFromParts(year, month, day, hour, minute).getTime();
  const wallMillis = (instant: number): number => {
    const parts = calendarDateTimePartsInTimeZone(new Date(instant), timeZone);
    return utcDateFromParts(parts.year, parts.month, parts.day, parts.hour, parts.minute, parts.second).getTime();
  };
  // Capture both offsets around transitions, including a full-day skipped date.
  const step = 6 * 60 * 60 * 1000;
  const probes: number[] = [];
  const candidates = new Set<number>();
  for (let offset = -48; offset <= 48; offset += 6) {
    const probe = target + offset * 60 * 60 * 1000;
    probes.push(probe);
    const candidate = target - (wallMillis(probe) - probe);
    if (wallMillis(candidate) === target) candidates.add(candidate);
  }
  if (candidates.size > 0) return formatUtcInstant(new Date(Math.min(...candidates)));
  // No exact occurrence: locate the transition edge that jumps over the target.
  for (const lower of probes) {
    let left = lower;
    let right = lower + step;
    if (wallMillis(left) >= target || wallMillis(right) <= target) continue;
    while (right - left > 1) {
      const middle = Math.floor((left + right) / 2);
      if (wallMillis(middle) >= target) right = middle;
      else left = middle;
    }
    return formatUtcInstant(new Date(right));
  }
  throw new Error(`Cannot resolve release time in timezone ${timeZone}`);
}
